import React, { useEffect, useState, useContext } from "react";
import { Table, Popover, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, IconButton, Autocomplete, TextField } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import moment from "moment";
import { AuthContext } from "../../../context/AuthContext";
import { summary } from "../../../Actions/summary";
import useWindowDimemsions from "../../../hooks/useWindowDimesnions";
import SearchBar from "../../Common/SearchBar/SearchBar";
import "./DashBoardCards.css";
import NavigateMenu from "../../../Actions/NavigateMenu";

const DashBoardTable = ({ tableData, alertsData, showOnlyNifty50, setShowOnlyNifty50, searchFilter, optionsData, onSearchChange, updateDate1, updateDate2 }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowDimemsions();
  const lgWidth = 991;
  const { userInfo } = useContext(AuthContext);
  const isLoggedIn = userInfo !== null;
  const headerFilter = useSelector((state) => state.FilterSummary.filter);
  const theme = useTheme();
  const isMediumUp = useMediaQuery(theme.breakpoints.up("md"));

  const [updatedData, setUpdatedData] = useState([]);
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [sortBy, setSortBy] = useState("Default");
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [legendModalOpen, setLegendModalOpen] = useState(false);

  const removeSpace = (item) => {
    let str = item;
    if (str.indexOf(" ") >= 0) {
      str = str.split(" ").join("\u00A0");
    }
    return str;
  };

  const toolTipTitle = (
    <div>
      <div style={{ marginBottom: "8px" }}>
        The background color of the boxes indicates the percentile ranking of a
        value over the past 30 days
      </div>
      <div className="table-legend-only">
        <div className="table-legend-item">
          <i className="fas fa-stop pe-2" style={{ color: "#84FED2" }}></i>
          <span className="pe-2">Top&nbsp;75&nbsp;-&nbsp;100%</span>
        </div>

        <div className="table-legend-item">
          <i className="fas fa-stop pe-2" style={{ color: "#FFE7A8" }}></i>
          <span className="pe-2">Middle&nbsp;25&nbsp;-&nbsp;75%</span>
        </div>

        <div className="table-legend-item">
          <i className="fas fa-stop pe-2" style={{ color: "#FFC7D4" }}></i>
          <span className="pe-2">Bottom&nbsp;0&nbsp;-&nbsp;25%</span>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (isLoggedIn) {
      setAdditionalFilters([
        {
          value: "Momentum_Value_Strategy_Stock",
          label: "Momentum & Value Strategy Stocks",
        },
      ]);
    } else {
      setAdditionalFilters([]);
    }
  }, [isLoggedIn]);

  useEffect(() => {

    const updatedData = tableData.map((summary) => {
      const alerts = alertsData[summary.Stock_Ticker];
      const alertsCount = alerts ? alerts.length : 0;
      const alertsColor = `rgb(0, 153, 51, ${alertsCount / 10})`;
      return {
        ...summary,
        Today_News_Alerts: alertsCount,
        Today_News_Alert_colors: alertsColor,
        alerts: alerts || [],
      };
    });
    setUpdatedData(updatedData);
  }, [tableData]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const isSameKey = prevConfig.key === key;
      const direction =
        isSameKey && prevConfig.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  const filteredAndSortedData = () => {
    let filtered = updatedData;

    // Apply search filter (from SearchBar component)
    if (searchFilter) {
      filtered = filtered.filter((item) =>
        item.Stock_Ticker.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    // Apply filter by (if needed in future)
    // if (filterBy !== "All") {
    //   filtered = filtered.filter(...);
    // }

    // Apply sorting
    const { key, direction } = sortConfig;
    if (!key || sortBy === "Default") return filtered;

    return [...filtered].sort((a, b) => {
      const getValue = (item) => {
        if (key === "Trading_Amount") {
          return item.Today_Volume / 10000000;
        } else if (key === "Today_News_Sentiment") {
          return item.Today_News_Sentiment === "NA"
            ? -Infinity
            : item.Today_News_Sentiment;
        } else {
          return item[key];
        }
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleTickerChange = (ticker) => {
    window.localStorage.setItem("tickerId", ticker);
  };

  const handleSectorChange = (event) => {
    dispatch(summary(event.target.value));
  };

  // Header style components
  const listItemStyle =
    width && width > lgWidth
      ? {}
      : { fontSize: "small", minHeight: "2px !important" };

  const selectStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      height: "2.5rem",
    },
  };

  const renderAlertsPopover = (alerts) => {
    const alertTypes = {
      ADX_change: "ADX_Change",
      Top_stories_using_TSA_count: "Top Stories",
      price_change_alerts: "Price Change",
      StockVolume_change: "Stock Volume Change",
      events_with_summary: "Events",
      news_count: "News Count",
      ATR_change: "ATR Change",
    };

    return (
      <Popover id="alerts-popover" className="custom-popover">
        <Popover.Body>
          {alerts.length > 0 ? (
            <div>
              {alerts.map((alert, index) => {
                const [alertKey, alertData] = Object.entries(alert)[0];
                return (
                  <div key={index}>
                    <ul>
                      {Object.entries(alertData).map(([key, value], idx) => {
                        if (alertKey === "price_change_alerts") {
                          return (
                            <div key={idx}>
                              {key === "alerts_open_highest" ? (
                                <div className="title">
                                  <strong>Open Price :{" "}</strong>
                                  <span style={{ color: "green" }}>High</span>{" "}
                                  {value.live_day_open.toFixed(2)}
                                </div>
                              ) : key === "alerts_open_lowest" ? (
                                <div className="title">
                                  <strong>Open Price :{" "}</strong>
                                  <span style={{ color: "red" }}>Low</span>{" "}
                                  {value.live_day_open.toFixed(2)}
                                </div>
                              ) : key === "alerts_close_highest" ? (
                                <div className="title">
                                  <strong>Close Price :{" "}</strong>
                                  <span style={{ color: "green" }}>High</span>{" "}
                                  {value.live_day_close.toFixed(2)}
                                </div>
                              ) : key === "alerts_close_lowest" ? (
                                <div className="title">
                                  <strong>Close Price :{" "}</strong>
                                  <span style={{ color: "red" }}>Low</span>{" "}
                                  {value.live_day_close.toFixed(2)}
                                </div>
                              ) : key === "alerts_price_change" ? (
                                <div className="title">
                                  <strong>Percentage Change :{" "} </strong>
                                  {value.percentage_change > 0 ? (
                                    <span style={{ color: "green" }}>
                                      {value.percentage_change.toFixed(2)}%
                                    </span>
                                  ) : (
                                    <span style={{ color: "red" }}>
                                      {value.percentage_change.toFixed(2)}%
                                    </span>
                                  )}
                                </div>
                              ) : key === "top_n_price_change" ? (
                                <div className="title">
                                  <strong>Percentage Change :{" "} </strong>
                                  {value.percentage_change > 0 ? (
                                    <span style={{ color: "green" }}>
                                      {value.percentage_change.toFixed(2)}%
                                    </span>
                                  ) : (
                                    <span style={{ color: "red" }}>
                                      {value.percentage_change.toFixed(2)}%
                                    </span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        if (alertKey === "ADX_change") {
                          return (
                            <div key={idx}>
                              {key === "weak_trend" ? (
                                <div className="title">
                                  <strong>ADX Change :{" "}</strong>
                                  <span style={{ color: "red" }}>Weak Trend</span>{" "}
                                  {parseInt(value.ADX).toFixed(2)}
                                </div>
                              ) : key === "strong_trend" ? (
                                <div className="title">
                                  <strong>ADX Change :{" "}</strong>
                                  <span style={{ color: "green" }}>Strong Trend</span>{" "}
                                  {parseInt(value.ADX).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        if (alertKey === "ATR_change") {
                          return (
                            <div key={idx}>
                              {key === "low_volatility" ? (
                                <div className="title">
                                  <strong>ATR Change :{" "}</strong>
                                  <span style={{ color: "red" }}>Low Volatility</span>{" "}
                                  {parseInt(value.Last_ATR).toFixed(2)}
                                </div>
                              ) : key === "high_volatility" ? (
                                <div className="title">
                                  <strong>ATR Change :{" "}</strong>
                                  <span style={{ color: "green" }}>
                                    High Volatility
                                  </span>{" "}
                                  {parseInt(value.Last_ATR).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        if (alertKey === "dpo_alert") {
                          return (
                            <div key={idx}>
                              {key === "Downward_cycle" ? (
                                <div className="title">
                                  <strong>DPO Change :{" "}</strong>
                                  <span style={{ color: "red" }}>Downwards</span>{" "}
                                  {parseInt(value.Current_dpo).toFixed(2)}
                                </div>
                              ) : key === "high_volatility" ? (
                                <div className="title">
                                  <strong>DPO Change :{" "}</strong>
                                  <span style={{ color: "green" }}>
                                    Upwards
                                  </span>{" "}
                                  {parseInt(value.Current_dpo).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        if (alertKey === "AD_alert") {
                          return (
                            <div key={idx}>
                              {key === "Distribution" ? (
                                <div className="title">
                                  <strong>AD Alert : {" "}</strong>
                                  <span style={{ color: "red" }}>Distribution</span>{" "}
                                  {value["Current A/D"].toFixed(2)}
                                </div>
                              ) : key === "Accumulation" ? (
                                <div className="title">
                                  <strong>AD Alert : {" "}</strong>
                                  <span style={{ color: "green" }}>
                                    Accumulation
                                  </span>{" "}
                                  {value["Current A/D"].toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }


                        if (alertKey === "stock_vs_index") {
                          return (
                            <div key={idx}>
                              {Array.isArray(value) && value.length > 0 ? (
                                value.map((indexAlert, subIdx) => (
                                  <div key={subIdx}>
                                    <div className="title">
                                      <strong>Stock vs Index :</strong>

                                      <div>Alert: {indexAlert.Alert}</div>
                                      <div>Index: {indexAlert.Index}</div>
                                      <div>
                                        Index % Change:{" "}
                                        {indexAlert.Index_pct_change > 0 ? (
                                          <span style={{ color: "green" }}>
                                            {indexAlert.Index_pct_change.toFixed(2)}%
                                          </span>
                                        ) : (
                                          <span style={{ color: "red" }}>
                                            {indexAlert.Index_pct_change.toFixed(2)}%
                                          </span>
                                        )}
                                      </div>
                                      <div>
                                        Stock % Change:{" "}
                                        {indexAlert.Stock_pct_change > 0 ? (
                                          <span style={{ color: "green" }}>
                                            {indexAlert.Stock_pct_change.toFixed(2)}%
                                          </span>
                                        ) : (
                                          <span style={{ color: "red" }}>
                                            {indexAlert.Stock_pct_change.toFixed(2)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                null
                              )}
                            </div>
                          );
                        }



                        if (alertKey === "Top_stories_using_TSA_count") {
                          return (
                            <div key={idx} className="title">
                              <strong>Top Stories :</strong>
                              <div>{value.title}</div>
                              {/* <div>
                                <a
                                  href={value.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Read More
                                </a>
                              </div> */}
                            </div>
                          );
                        }


                        if (alertKey === "events_with_summary") {
                          return (
                            <div key={idx}>
                              <div className="title">Max Event :{" "} <span style={{ fontStyle: "bold" }}>{value.max_event}</span></div>
                              <div className="title">Event Count :{" "} <span style={{ fontStyle: "bold" }}>{value.max_event_count}</span></div>
                            </div>
                          );
                        }

                        if (alertKey === "StockVolume_change") {
                          return (
                            <div key={idx}>
                              {key === "Stock_volume_high" ? (
                                <div className="title">
                                  <strong>Stock Volume :{" "}</strong>
                                  <span style={{ color: "green" }}>High</span>{" "}
                                  {parseInt(value["Current Volume"]).toFixed(2)}
                                </div>
                              ) : key === "Stock_volume_low" ? (
                                <div className="title">
                                  <strong>Stock Volume :{" "}</strong>
                                  <span style={{ color: "red" }}>Low</span>{" "}
                                  {parseInt(value["Current Volume"]).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }

                        if (alertKey === "news_count") {
                          return (
                            <div key={idx}>
                              <div className="title"> <strong>News Count :</strong>  <span style={{ fontStyle: "bold" }}>{value.news_count}</span></div>
                            </div>
                          );
                        }
                        if (alertKey === "Momentum_change") {
                          return (
                            <div key={idx}>
                              {key === "Oversold" ? (
                                <div className="title">
                                  <strong>Momentum Change :{" "}</strong>
                                  <span style={{ color: "red" }}>Oversold</span>{" "}
                                </div>
                              ) : key === "Overbought" ? (
                                <div className="title">
                                  <strong>Momentum Change :{" "}</strong>
                                  <span style={{ color: "green" }}>Overbought</span>{" "}
                                </div>
                              ) : null}
                            </div>
                          );
                        }
                        if (alertKey === "VWAP_change") {
                          return (
                            <div key={idx}>
                              {key === "Downtrend" ? (
                                <div className="title">
                                  <strong>VWAP :{" "}</strong>
                                  <span style={{ color: "red" }}>Down</span>{" "}
                                  {parseInt(value.current_vwap).toFixed(2)}
                                </div>
                              ) : key === "Uptrend" ? (
                                <div className="title">
                                  <strong>VWAP :{" "}</strong>
                                  <span style={{ color: "green" }}>Up</span>{" "}
                                  {parseInt(value.current_vwap).toFixed(2)}
                                </div>
                              ) : null}
                            </div>
                          );
                        }
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          ) : (
            "No alerts available"
          )}
        </Popover.Body>
      </Popover>
    );
  };

  const columns = [
    { key: "Stock_Ticker", label: "Stock Ticker" },
    {
      key: "Today_News_Alerts",
      label: "Total Alerts",
      colorKey: "Today_News_Alert_colors",
      content: (summary) => summary.alerts.length || "0",
    },
    {
      key: "Trading_Amount",
      label: "Volume (Crs)",
      colorKey: "Today_Volume_colors",
      content: (summary) => (summary.Today_Volume / 10000000).toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    },
    {
      key: "Today_Returns",
      label: "Returns",
      colorKey: "Today_Returns_colors",
      content: (summary) => `${summary.Today_Returns} %`,
    },
    {
      key: "Today_News_Volume",
      label: "News Volume",
      colorKey: "Today_News_Volume_colors",
      content: (summary) => summary.Today_News_Volume,
    },
    {
      key: "Today_News_Sentiment",
      label: "Sentiments",
      colorKey: "Today_News_Sentiment_colors",
      content: (summary) =>
        summary.Today_News_Sentiment.toString() === "NA"
          ? "No Data"
          : summary.Today_News_Sentiment,
    },
  ];

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 4.5L6 1.5L9 4.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 7.5L6 10.5L9 7.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    return sortConfig.direction === "asc" ? (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 4.5L6 1.5L9 4.5" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 7.5L6 10.5L9 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 4.5L6 1.5L9 4.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 7.5L6 10.5L9 7.5" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  return (
    <div className="main-div">
      {/* Info Icon, Tooltip and Modal */}
      <div className="info-icon-container">
        <IconButton
          onClick={() => setInfoModalOpen(true)}
          className="info-icon-button"
          aria-label="Update Information"
        >
          <InfoOutlinedIcon />
        </IconButton>
        <IconButton
          className="info-icon-button tooltip-icon-button"
          aria-label="Color Legend"
          onClick={() => setLegendModalOpen(true)}
        >
          <HelpOutlineIcon />
        </IconButton>
      </div>

      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Information</DialogTitle>
        <DialogContent>
          <div className="update-info-content">
            <div className="update-info-item">
              <strong>News Updated on:</strong>{" "}
              {updateDate1
                ? removeSpace(moment(updateDate1).format("DD-MMM-YYYY HH:mm"))
                : "N/A"}
            </div>
            <div className="update-info-item">
              <strong>Stocks Updated on:</strong>{" "}
              {updateDate2
                ? removeSpace(moment(updateDate2).format("DD-MMM-YYYY HH:mm"))
                : "N/A"}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={legendModalOpen}
        onClose={() => setLegendModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Color Legend</DialogTitle>
        <DialogContent>{toolTipTitle}</DialogContent>
      </Dialog>

      {/* Search and Filter Controls */}
      <div className="dashboard-controls">
        <div className="search-container">
          <Autocomplete
            freeSolo
            options={optionsData}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option && typeof option === 'object' && option.label) return option.label;
              return String(option);
            }}
            value={searchFilter}
            onChange={(event, newValue) => {
              const normalizedValue = typeof newValue === 'string' ? newValue : (newValue?.label || newValue?.value || '');
              onSearchChange(normalizedValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by Ticker"
                variant="outlined"
                size="small"
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            )}
          />
        </div>
        <div className="filter-container">
          <FormControl fullWidth variant="outlined" sx={selectStyles}>
            <InputLabel
              id="select-label-1"
              style={{ top: "-6px", fontSize: "14px", fontWeight: "bold" }}
            >
              Filter By
            </InputLabel>
            <Select
              className="filterby"
              labelId="select-label-1"
              label="Filter By"
              value={showOnlyNifty50 || "all"}
              onChange={(event) => {
                if (setShowOnlyNifty50) {
                  setShowOnlyNifty50(event.target.value);
                }
              }}
            >
              {[
                { value: "all", label: "All" },
                { value: "nifty", label: "Nifty50" },
                ...additionalFilters,
              ].map((item) => (
                <MenuItem
                  key={item.label}
                  value={item.value}
                  sx={listItemStyle}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="sort-container">
          <FormControl fullWidth variant="outlined" sx={selectStyles}>
            <InputLabel
              id="select-label-2"
              style={{ top: "-6px", fontSize: "14px", fontWeight: "bold" }}
            >
              Sort By
            </InputLabel>
            <Select
              className="sort-by"
              labelId="select-label-2"
              label="Sort By"
              value={headerFilter || "All"}
              onChange={handleSectorChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="All" sx={listItemStyle}>
                All Sectors
              </MenuItem>
              <MenuItem value="AUTO ANCILLARIES" sx={listItemStyle}>
                Auto Ancillaries
              </MenuItem>
              <MenuItem value="AUTOMOBILE" sx={listItemStyle}>
                Automobile
              </MenuItem>
              <MenuItem value="BEARINGS" sx={listItemStyle}>
                Bearings
              </MenuItem>
              <MenuItem
                value="CAPITAL GOODS-NON ELECTRICAL EQUIPMENT"
                sx={listItemStyle}
              >
                Capital Goods-Non Electrical Equipment
              </MenuItem>
              <MenuItem value="CHEMICALS" sx={listItemStyle}>
                Chemicals
              </MenuItem>
              <MenuItem value="CONGLOMERATE" sx={listItemStyle}>
                Conglomerate
              </MenuItem>
              <MenuItem value="CONSTRUCTION" sx={listItemStyle}>
                Construction
              </MenuItem>
              <MenuItem value="CONSTRUCTION MATERIALS" sx={listItemStyle}>
                Construction Materials
              </MenuItem>
              <MenuItem value="CONSUMER DURABLES" sx={listItemStyle}>
                Consumer Durables
              </MenuItem>
              <MenuItem value="FINANCIAL SERVICES" sx={listItemStyle}>
                Financial Services
              </MenuItem>
              <MenuItem
                value="FAST MOVING CONSUMER GOODS"
                sx={listItemStyle}
              >
                FMCG
              </MenuItem>
              <MenuItem value="HEALTHCARE" sx={listItemStyle}>
                Healthcare
              </MenuItem>
              <MenuItem value="IT" sx={listItemStyle}>
                IT
              </MenuItem>
              <MenuItem value="MISCELLANEOUS" sx={listItemStyle}>
                Miscellaneous
              </MenuItem>
              <MenuItem value="METALS AND MINING" sx={listItemStyle}>
                Metals and Mining
              </MenuItem>
              <MenuItem
                value="OIL GAS AND CONSUMABLE FUEL"
                sx={listItemStyle}
              >
                Oil Gas and Consumable Fuel
              </MenuItem>
              <MenuItem value="POWER" sx={listItemStyle}>
                Power
              </MenuItem>
              <MenuItem value="SERVICES" sx={listItemStyle}>
                Services
              </MenuItem>
              <MenuItem value="TELECOMMUNICATION" sx={listItemStyle}>
                Telecommunication
              </MenuItem>
              {headerFilter === "" && (
                <MenuItem disabled value="" sx={listItemStyle}>
                  <span
                    style={{
                      opacity: "0.5",
                      fontWeight: "500",
                      color: "gray",
                    }}
                  >
                    Select Sector
                  </span>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </div>
      </div>
      <div className="dashboard-table-wrapper">
        <Table responsive className="dashboard-table">
          <thead className="table-head-column">
            <tr className="table-header-height">
              <th className="table-header-cell">
                <div className="header-content">
                  <span className="header-label">S.No</span>
                </div>
              </th>
              {columns.map(({ key, label }) => (
                <th
                  key={key}
                  className="table-header-cell"
                  onClick={() => {
                    handleSort(key);
                    setSortBy(key);
                  }}
                >
                  <div className="header-content">
                    <span className="header-label">{label}</span>
                    {key !== "Stock_Ticker" && (
                      <span className="sort-icon">{getSortIcon(key)}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData().map((summary, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td
                  onClick={() => {
                    handleTickerChange(summary.Stock_Ticker);
                    navigate("/detail");
                    dispatch(NavigateMenu(0));
                  }}
                  style={{ cursor: "pointer" }}
                  className="ticker-cell"
                >
                  <div className="ticker-content">
                    <img
                      src={`${process.env.REACT_APP_COMPANY_DOC}/Marquee_logos/${summary.Stock_Ticker?.split(".")[0]
                        }.svg`}
                      alt="logo"
                      className="ticker-logo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <span className="ticker-name">{summary.Stock_Ticker}</span>
                  </div>
                </td>
                {columns.slice(1).map((column, colIndex) => {
                  const colorValue = summary[column.colorKey];
                  let textColor = "#1f2937"; // default dark gray
                  let bgColor = "transparent";

                  if (colorValue) {
                    if (typeof colorValue === 'string') {
                      // Handle RGB with alpha: "rgb(0, 153, 51, 0.5)" -> extract and darken
                      if (colorValue.startsWith('rgb')) {
                        const rgbMatch = colorValue.match(/rgb\((\d+),\s*(\d+),\s*(\d+)/);
                        if (rgbMatch) {
                          const r = parseInt(rgbMatch[1]);
                          const g = parseInt(rgbMatch[2]);
                          const b = parseInt(rgbMatch[3]);
                          bgColor = `rgba(${r}, ${g}, ${b}, 0.14)`;

                          // Determine color type and apply dark version
                          // Green colors (high g, low r and b) -> dark green
                          if (g > r && g > b && g > 100) {
                            textColor = "#059669"; // dark green (emerald-600)
                          }
                          // Red/Pink colors (high r, low g and b) -> dark red
                          else if (r > g && r > b && r > 100) {
                            textColor = "#dc2626"; // dark red (red-600)
                          }
                          // Yellow/Orange colors (high r and g, low b) -> dark orange/yellow
                          else if (r > 150 && g > 150 && b < 100) {
                            textColor = "#d97706"; // dark orange (amber-600)
                          }
                          // Blue colors -> dark blue
                          else if (b > r && b > g && b > 100) {
                            textColor = "#2563eb"; // dark blue (blue-600)
                          }
                          // Default: use darker version of the color
                          else {
                            // Darken by reducing values
                            const darkR = Math.max(0, Math.floor(r * 0.6));
                            const darkG = Math.max(0, Math.floor(g * 0.6));
                            const darkB = Math.max(0, Math.floor(b * 0.6));
                            textColor = `rgb(${darkR}, ${darkG}, ${darkB})`;
                          }
                        } else {
                          textColor = colorValue;
                        }
                      } else {
                        // Hex color - convert to darker version if needed
                        if (colorValue.startsWith('#')) {
                          // For hex colors, we'll use predefined dark colors based on the hex
                          const hex = colorValue.replace('#', '');
                          const r = parseInt(hex.substr(0, 2), 16);
                          const g = parseInt(hex.substr(2, 2), 16);
                          const b = parseInt(hex.substr(4, 2), 16);
                          bgColor = `rgba(${r}, ${g}, ${b}, 0.14)`;

                          if (g > r && g > b && g > 100) {
                            textColor = "#059669"; // dark green
                          } else if (r > g && r > b && r > 100) {
                            textColor = "#dc2626"; // dark red
                          } else if (r > 150 && g > 150 && b < 100) {
                            textColor = "#d97706"; // dark orange
                          } else {
                            textColor = colorValue; // use as is
                          }
                        } else {
                          textColor = colorValue;
                        }
                      }
                    }
                  }

                  // Special handling for "No Data" or zero values - use dark gray
                  const content = column.content(summary);
                  if (content === "No Data" || content === 0 || content === "0") {
                    textColor = "#6b7280"; // dark gray for no data
                    bgColor = "transparent";
                  }

                  return (
                    <td key={colIndex}>
                      {column.key === "Today_News_Alerts" ? (
                        <OverlayTrigger
                          trigger="hover"
                          placement="right"
                          overlay={renderAlertsPopover(summary.alerts)}
                        >
                          <span
                            className="table-value table-value-box clickable-value"
                            style={{ color: textColor, backgroundColor: bgColor }}
                            onClick={() => {
                              handleTickerChange(summary.Stock_Ticker);
                              navigate("/trending", {
                                state: { ticker: summary.Stock_Ticker },
                              });
                              dispatch(NavigateMenu(2));
                            }}
                          >
                            {content}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span
                          className="table-value table-value-box"
                          style={{ color: textColor, backgroundColor: bgColor }}
                        >
                          {content}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

    </div>
  );
};

export default DashBoardTable;
